import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SquareTerminal, Server } from 'lucide-react';
import { chordKeys } from './terminalPaneShortcuts';
import { useTerminalConfigStore } from '../../store/terminalConfigStore';
import { PaneIcon } from './TerminalPaneHeader';
import { launchProfile } from './launchProfile';
import LayoutThumbnail from './LayoutThumbnail';
import { openSavedLayout } from './layoutActions';

function Keys({ keys }) {
  return (
    <span className="term-empty-chord">
      {keys.map((k, i) => (
        <React.Fragment key={k}>
          {i > 0 && (
            <span className="term-empty-plus" aria-hidden>
              +
            </span>
          )}
          <kbd>{k}</kbd>
        </React.Fragment>
      ))}
    </span>
  );
}

/**
 * The Terminal with no tabs: a centred card on the dark surface with the
 * one-click starts (`actions` — default shell first; agents and layouts
 * are added by the view when available), then the pane shortcuts.
 */
export default function TerminalEmpty({ onOpenShell, onOpenSsh, actions = null, footer = null }) {
  const { t } = useTranslation();
  const profiles = useTerminalConfigStore((s) => s.profiles);
  const detection = useTerminalConfigStore((s) => s.detection);
  const layouts = useTerminalConfigStore((s) => s.layouts);
  useEffect(() => {
    const st = useTerminalConfigStore.getState();
    st.load().then(() => st.detect());
  }, []);
  // The first installed, visible agent gets a one-click start here.
  const agent = profiles.find((p) => !p.hidden && detection[p.command]);
  const rows = [
    ['splitRight', t('terminalExt.splitRight', 'Split right')],
    ['splitDown', t('terminalExt.splitDown', 'Split down')],
    ['focus', t('terminalExt.shortcutFocusPane', 'Move between panes')],
    ['resize', t('terminalExt.resizePanes', 'Resize panes')],
    ['zoom', t('terminalExt.zoomPane', 'Zoom pane')],
    ['close', t('terminalExt.closePane', 'Close pane')],
  ];
  return (
    <div className="term-empty">
      <div className="term-empty-card">
        <div className="term-empty-title">
          <SquareTerminal size={18} aria-hidden />
          {t('terminalExt.emptyTitle', 'Start a terminal')}
        </div>
        <div className="term-empty-actions">
          <button type="button" className="term-empty-btn" onClick={onOpenShell}>
            <span className="term-empty-btn-icon">
              <SquareTerminal size={18} aria-hidden />
            </span>
            <span className="term-empty-btn-text">
              {t('terminalExt.defaultShell', 'Default shell')}
              <span className="term-empty-btn-sub">{t('terminalExt.defaultShellSub', 'A local terminal')}</span>
            </span>
          </button>
          {agent && (
            <button type="button" className="term-empty-btn" onClick={() => launchProfile(agent, 'tab')}>
              <span className="term-empty-btn-icon">
                <PaneIcon pane={{ icon: agent.icon, profileId: agent.id }} size={16} />
              </span>
              <span className="term-empty-btn-text">
                {agent.name}
                <span className="term-empty-btn-sub">{t('terminalExt.agentSub', 'Coding agent, new tab')}</span>
              </span>
            </button>
          )}
          {actions}
          <button type="button" className="term-empty-btn" onClick={onOpenSsh}>
            <span className="term-empty-btn-icon">
              <Server size={18} aria-hidden />
            </span>
            <span className="term-empty-btn-text">
              {t('terminalExt.sshConnection', 'SSH connection')}
              <span className="term-empty-btn-sub">{t('terminalExt.sshSub', 'From your vault')}</span>
            </span>
          </button>
        </div>
        {layouts.length > 0 && (
          <>
            <div className="term-empty-section">{t('terminalExt.savedLayoutsTitle', 'Saved layouts')}</div>
            <div className="term-empty-actions">
              {layouts.slice(0, 3).map((l) => (
                <button key={l.id} type="button" className="term-empty-btn" onClick={() => openSavedLayout(l)}>
                  <span className="term-empty-btn-icon">
                    <LayoutThumbnail tree={l.tree} saved width={28} height={18} />
                  </span>
                  <span className="term-empty-btn-text">
                    {l.name}
                    <span className="term-empty-btn-sub">{t('terminalExt.paneCount', '{{count}} panes', { count: (l.slots || []).length })}</span>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
        {footer}
        <div className="term-empty-section">{t('terminalExt.shortcutsTitle', 'Shortcuts')}</div>
        <div className="term-empty-keys" role="list">
          {rows.map(([action, label]) => (
            <div key={action} className="term-empty-key-row" role="listitem">
              <span>{label}</span>
              <Keys keys={chordKeys(action)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
